<template>
  <div class="tw:flex tw:justify-between tw:items-center tw:gap-2 tw:font-serif recipeRow">
    <div class="left-block tw:flex tw:flex-col tw:gap-0.5" :class="[recipe.unclaimedModifier > 0 ? 'fullWidth' : null]">
      <vue-countdown :time="countdownTimestamp" @progress="handleProgress"> </vue-countdown>

      <div class="tw:gap-1 recipeRowInfo">
        <img :src="getIcon(recipe.recipeId, recipe.products[0].tokenAddress)" :alt="tokenMetadataComputed?.label" />
        <span class="tokenName">
          {{ $t(`${recipeIdPrefix}${recipe?.recipeId}`) }} (<span>{{ recipe.unclaimedModifier }}</span>
          /
          {{ recipe.multiplyModifier }})
        </span>
      </div>
      <div :class="['recipeRowProgress', percent <= CRAFTING_PERCENT ? 'default' : 'halfDone']">
        <div
          :class="['progressFill', percent <= CRAFTING_PERCENT ? 'default' : 'halfDone']"
          :style="{ width: `${percent > 100 ? 100 : percent}%` }"
        />
      </div>
    </div>
    <button
      :disabled="isLoadingClaimButton || recipe.unclaimedModifier === 0"
      class="tw:font-serif! btn-primary"
      type="button"
      @click="claimCraftedTokens()"
    >
      <span>{{ $t('craftingClaim') }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { Crafting__factory } from 'public/abi/evm/types';
import useProvider from '~/composables/useProvider';
import { calculateTimeLeftPercent } from '~/utils/crafting';
import { CRAFTING_PERCENT, ERRORS_CONFIG } from '~/utils/constants';
import type { UnclaimedRecipeProps } from '~/types/crafting';
import useSendContractMethod from '~/composables/useSendContractMethod';
import VueCountdown from '@chenfengyuan/vue-countdown';
import { tokenMetaData } from '~/utils';

const { t } = useI18n();
const { blockchain } = useEnvs();
const { sendContractMethod } = useSendContractMethod();
const store = useMainStore();
const config = useRuntimeConfig();
const { getSigner } = useProvider();

const props = defineProps<{
  recipe: UnclaimedRecipeProps;
  icon: string;
}>();

const isLoadingClaimButton = defineModel<boolean>({ default: false });
const recipeIdPrefix = computed(() => {
  const env = config?.public.env;
  return env === 'prod' ? 'prodRecipeId' : env === 'demo' ? 'demoRecipeId' : 'testnetRecipeId';
});

const craftStartStamp = computed(() => new Date(props.recipe.craftStartTime).getTime());
const craftEndStamp = computed(
  () => craftStartStamp.value + props.recipe.craftDuration * props.recipe.multiplyModifier * 1000
);

const countdownTimestamp = computed(() => craftEndStamp.value - Date.now());
const tokenMetadataComputed = computed(() => {
  if (!props.recipe) return null;

  return tokenMetaData(blockchain.contracts, props.recipe.products[0].tokenAddress);
});

const percent = ref<number>(calculateTimeLeftPercent(craftStartStamp.value, craftEndStamp.value, Date.now()));

const handleProgress = () => {
  percent.value = calculateTimeLeftPercent(craftStartStamp.value, craftEndStamp.value, Date.now());
};

const claimCraftedTokens = async () => {
  isLoadingClaimButton.value = true;
  try {
    const { recipeId, userRecipeIndex } = props.recipe;
    const claimTx = await sendContractMethod({
      contract: Crafting__factory.connect(blockchain.contracts.crafting, await getSigner()),
      methodName: 'claimRecipe',
      args: [recipeId, userRecipeIndex]
    });

    await claimTx?.wait();
    if (!claimTx?.hash) return;

    saveHashToLocalStorage(`${t('claimingRecipesNotification')}`, claimTx.hash);

    store.updateVersion();
    modifyHistoryHashStatus(claimTx.hash, 'Done');
  } catch (error) {
    ElNotification.error(ERRORS_CONFIG.apiErrors.claimError.generic);
  } finally {
    store.updateVersion();
  }
};

onBeforeUnmount(() => {
  isLoadingClaimButton.value = false;
});
</script>

<style lang="scss" scoped>
.recipeRow {
  button {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    padding: 4px 24px;
    width: auto;
    min-width: max-content;
    line-height: 0.5em;
    white-space: nowrap;
  }

  .left-block {
    width: 65%;
  }

  .fullWidth {
    width: 100%;
  }

  .recipeRowInfo {
    display: flex;
    align-items: center;
    font-size: 18px;
    font-weight: 600;

    img {
      width: 33px;
      height: 33px;

      &[alt='nftChristmasShip'] {
        border-radius: 5px;
      }
    }

    .tokenName {
      color: #fff;
      text-align: left;
      font-size: 16px;

      span {
        color: #49ddaa;
      }
    }
  }

  .recipeRowProgress {
    position: relative;
    border-radius: 8px;
    border: 1px solid #49ddaa;
    border-radius: 8px;
    height: 6px;
    overflow: hidden;
    display: flex;
    justify-content: space-evenly;
    transition: all 0.3s ease;
    background: linear-gradient(0deg, #010303, #17343e);

    .progressFill {
      height: 100%;
      width: 70%;
      position: absolute;
      top: 0;
      left: 0;
      background: linear-gradient(90deg, #01c0fe 0%, #60ff7c 100%);
    }
  }
}
</style>
